import { isEmpty } from '../../utils/utils';
import { expandFieldKeys } from './pecFieldSelection';

// Every field re-validates on each keystroke across every mounted section (see isStepPatternValid
// and validateFormSection), so the same handful of config patterns get compiled over and over —
// cached by pattern string, since patterns are static config, not user input.
const compiledPatternCache = new Map<string, RegExp | null>();

const compilePattern = (pattern: string): RegExp | null => {
    if (!compiledPatternCache.has(pattern)) {
        try {
            compiledPatternCache.set(pattern, new RegExp(pattern));
        } catch {
            compiledPatternCache.set(pattern, null);
        }
    }
    return compiledPatternCache.get(pattern) ?? null;
};

/**
 * True unless the field's config carries a `validationRegex` its value fails.
 *
 * An empty value always passes — emptiness is `isRequired`'s business, so an optional field left
 * blank stays valid. A `validationRegex` that is not a usable pattern fails open: a bad config
 * string must not take the drawer down.
 */
export const isFieldPatternValid = (config: any, value: any): boolean => {
    const pattern = config?.validationRegex;
    if (!pattern || isEmpty(value)) return true;
    return compilePattern(pattern)?.test(String(value)) ?? true;
};

/**
 * The same verdict across every field of one step's config, for the step's save gate. Each field's
 * keys come from expandFieldKeys, as the lock's do, so a range field is read at its min/max pair.
 */
export const isStepPatternValid = (stepConfig: any, formData: Record<string, any> = {}): boolean =>
    (stepConfig?.config ?? []).every((section: any) =>
        Object.entries(section?.config ?? {}).every(([fieldKey, fieldConfig]: [string, any]) =>
            expandFieldKeys(section?.sectionKey ?? '', fieldKey, fieldConfig, false).every((key) =>
                isFieldPatternValid(fieldConfig, formData[key]),
            ),
        ),
    );
